import {
    chartRefreshScheduler,
    computeStaleState,
    formatAge,
    FrameBudgetTracker,
    measureFrame,
    RingBuffer,
    getStaleThresholdMs,
    setSnapshot,
    getSnapshot,
    reportChartRenderMetric,
} from '../../charts/engine';
import { renderHeatmapGrid, renderLineChart, renderSparklineChart } from '../../charts/primitives';
import { fetchIndices } from '../../services/market-data';
import { isFeatureEnabled } from '../../services/runtime-config';

type IndexView = {
    symbol: string;
    name: string;
    last: number;
    pChange: number;
};

type PersistedIndicesSnapshot = {
    list: IndexView[];
    benchmarkSeries: number[];
  dispersionSeries: number[];
  rotationSeries: number[];
    symbolSeries: Record<string, number[]>;
    lastUpdatedAt: number;
};

const teardownMap = new WeakMap<HTMLElement, () => void>();

export function renderPhase2IndicesEngine(container: HTMLElement): void {
    if (!isFeatureEnabled('charts_v2_engine')) {
        container.innerHTML = '<div class="wm-empty">charts_v2_engine flag is disabled.</div>';
        return;
    }

    teardownMap.get(container)?.();

    container.innerHTML = `
      <div class="v2-chart-card glass-card">
        <div class="card-header">
          <h3>Phase 2 Engine: Indices</h3>
          <span class="text-xs text-muted">Tier A refresh</span>
        </div>

        <div class="v2-chart-grid">
          <div class="v2-chart-slot">
            <div class="v2-chart-title">Benchmark Trend</div>
            <canvas id="v2-indices-line" class="v2-chart-canvas"></canvas>
          </div>

          <div class="v2-chart-slot">
            <div class="v2-chart-title">Leaders and Laggards</div>
            <div id="v2-indices-board" class="v2-indices-board"></div>
          </div>

          <div class="v2-chart-slot v2-chart-slot--wide">
            <div class="v2-chart-title">Index Sparkline Strip</div>
            <div id="v2-indices-spark" class="v2-spark-strip"></div>
          </div>

          <div class="v2-chart-slot">
            <div class="v2-chart-title">Leadership Rotation</div>
            <canvas id="v2-indices-rotation" class="v2-chart-canvas"></canvas>
          </div>

          <div class="v2-chart-slot">
            <div class="v2-chart-title">Dispersion Stress Grid</div>
            <canvas id="v2-indices-dispersion" class="v2-chart-canvas"></canvas>
          </div>
        </div>

        <div class="v2-chart-meta">
          <span id="v2-indices-freshness" class="text-xs">Waiting for live index feed...</span>
          <span id="v2-indices-frame" class="text-xs text-muted" style="margin-left: var(--space-3);">Frame: --</span>
        </div>

        <div id="v2-indices-stale" class="v2-stale-overlay" style="display:none;">
          Index feed delayed. Rendering cached snapshot.
        </div>
      </div>
    `;

    const lineCanvas = container.querySelector('#v2-indices-line') as HTMLCanvasElement | null;
    const board = container.querySelector('#v2-indices-board') as HTMLElement | null;
    const sparkStrip = container.querySelector('#v2-indices-spark') as HTMLElement | null;
    const rotationCanvas = container.querySelector('#v2-indices-rotation') as HTMLCanvasElement | null;
    const dispersionCanvas = container.querySelector('#v2-indices-dispersion') as HTMLCanvasElement | null;
    const freshnessNode = container.querySelector('#v2-indices-freshness') as HTMLElement | null;
    const frameNode = container.querySelector('#v2-indices-frame') as HTMLElement | null;
    const staleNode = container.querySelector('#v2-indices-stale') as HTMLElement | null;

    if (!lineCanvas || !board || !sparkStrip || !rotationCanvas || !dispersionCanvas || !freshnessNode || !frameNode || !staleNode) return;

    const benchmarkSeries = new RingBuffer<number>(360);
    const dispersionSeries = new RingBuffer<number>(180);
    const rotationSeries = new RingBuffer<number>(180);
    const seriesBySymbol = new Map<string, RingBuffer<number>>();
    const frameTracker = new FrameBudgetTracker(200, 16);
    const staleThresholdMs = getStaleThresholdMs('A');
    const snapshotKey = 'phase2:indices-engine';

    let lastUpdatedAt: number | null = null;
    let lastList: IndexView[] = [];
    let previousLeaders: string[] = [];

    const computeDispersion = (list: IndexView[]): number => {
      if (list.length <= 1) return 0;
      const mean = list.reduce((sum, item) => sum + item.pChange, 0) / list.length;
      const variance = list.reduce((sum, item) => {
        const delta = item.pChange - mean;
        return sum + (delta * delta);
      }, 0) / list.length;
      return Math.sqrt(variance);
    };

    const computeRotation = (leaders: string[]): number => {
      if (previousLeaders.length === 0) return 0;
      const current = new Set(leaders);
      const prev = new Set(previousLeaders);
      let changed = 0;
      prev.forEach((symbol) => {
        if (!current.has(symbol)) changed += 1;
      });
      return (changed / Math.max(1, prev.size)) * 100;
    };

    const ensureSeries = (symbol: string): RingBuffer<number> => {
        const existing = seriesBySymbol.get(symbol);
        if (existing) return existing;
        const created = new RingBuffer<number>(180);
        seriesBySymbol.set(symbol, created);
        return created;
    };

    const renderBoard = (list: IndexView[]) => {
        const sorted = [...list].sort((a, b) => b.pChange - a.pChange);
        const leaders = sorted.slice(0, 3);
        const laggards = sorted.slice(-3).reverse();

        const renderRows = (rows: IndexView[]) => rows.map(item => {
            const tone = item.pChange >= 0 ? 'terminal-text--up' : 'terminal-text--down';
            return `<div class="terminal-list__row"><span>${item.symbol}</span><strong class="${tone}">${item.pChange.toFixed(2)}%</strong></div>`;
        }).join('');

        board.innerHTML = `
          <div class="v2-indices-col">
            <div class="v2-indices-col-title">Leaders</div>
            ${renderRows(leaders)}
          </div>
          <div class="v2-indices-col">
            <div class="v2-indices-col-title">Laggards</div>
            ${renderRows(laggards)}
          </div>
        `;
    };

    const renderSparkStrip = (list: IndexView[]) => {
        sparkStrip.innerHTML = list.slice(0, 8).map((item, idx) => (
            `<div class="v2-spark-item"><span>${item.symbol}</span><canvas id="v2-indices-spark-${idx}" class="v2-spark-canvas"></canvas></div>`
        )).join('');

        list.slice(0, 8).forEach((item, idx) => {
            const canvas = sparkStrip.querySelector(`#v2-indices-spark-${idx}`) as HTMLCanvasElement | null;
            if (!canvas) return;
            const values = ensureSeries(item.symbol).toArray();
            const color = item.pChange >= 0 ? '#15b57a' : '#d94736';
            renderSparklineChart(canvas, values, color);
        });
    };

    const draw = (list: IndexView[]) => {
        const sample = measureFrame('indices-engine', () => {
            renderLineChart(lineCanvas, benchmarkSeries.toArray(), '#5aa6ff');
        renderLineChart(rotationCanvas, rotationSeries.toArray(), '#ff9f43');
            renderBoard(list);
            renderSparkStrip(list);

        const sortedByAbs = [...list].sort((a, b) => Math.abs(b.pChange) - Math.abs(a.pChange));
        const cells = sortedByAbs.slice(0, 12).map((item, idx) => {
          const pressure = Math.max(-5, Math.min(5, item.pChange));
          return {
            x: idx % 6,
            y: Math.floor(idx / 6),
            value: pressure,
          };
        });
        renderHeatmapGrid(dispersionCanvas, cells, 6, 2);
        });

        frameTracker.push(sample.label, sample.durationMs);
        const avg = frameTracker.getAverageMs();
        const within = frameTracker.isWithinBudget(sample.durationMs);
        frameNode.textContent = `Frame: ${sample.durationMs.toFixed(2)}ms | avg ${avg.toFixed(2)}ms | budget ${within ? 'OK' : 'OVER'}`;

        reportChartRenderMetric({
            id: 'phase2-indices',
            label: 'Indices Engine',
            latestMs: sample.durationMs,
            averageMs: avg,
            budgetMs: frameTracker.getBudgetMs(),
            withinBudget: within,
        });

        const stale = computeStaleState(lastUpdatedAt, staleThresholdMs);
        staleNode.style.display = stale.stale ? 'flex' : 'none';
        freshnessNode.textContent = stale.stale
            ? `Stale: ${formatAge(stale.ageMs)} old`
            : `Live update ${new Date(lastUpdatedAt || Date.now()).toLocaleTimeString('en-IN')}`;
    };

    const persist = () => {
        const symbolSeries: Record<string, number[]> = {};
        seriesBySymbol.forEach((series, symbol) => {
            symbolSeries[symbol] = series.toArray();
        });

        setSnapshot<PersistedIndicesSnapshot>(snapshotKey, {
            list: lastList,
            benchmarkSeries: benchmarkSeries.toArray(),
        dispersionSeries: dispersionSeries.toArray(),
        rotationSeries: rotationSeries.toArray(),
            symbolSeries,
            lastUpdatedAt: lastUpdatedAt || Date.now(),
        });
    };

    const update = async () => {
        try {
            const indices = await fetchIndices();
            if (indices.length === 0) throw new Error('no index data');

            const list: IndexView[] = indices.slice(0, 10).map(item => ({
                symbol: item.symbol || item.name,
                name: item.name,
                last: item.last,
                pChange: item.pChange,
            }));

            list.forEach(item => ensureSeries(item.symbol).push(item.last));
            benchmarkSeries.push(list[0].last);
            dispersionSeries.push(computeDispersion(list));

            const leaders = [...list].sort((a, b) => b.pChange - a.pChange).slice(0, 3).map(item => item.symbol);
            rotationSeries.push(computeRotation(leaders));
            previousLeaders = leaders;

            lastList = list;
            lastUpdatedAt = Date.now();
            persist();
            draw(list);
        } catch {
            if (lastList.length > 0) draw(lastList);
        }
    };

    const persisted = getSnapshot<PersistedIndicesSnapshot>(snapshotKey);
    if (persisted?.list?.length) {
        persisted.benchmarkSeries.forEach(v => benchmarkSeries.push(v));
      persisted.dispersionSeries?.forEach(v => dispersionSeries.push(v));
      persisted.rotationSeries?.forEach(v => rotationSeries.push(v));
        Object.entries(persisted.symbolSeries).forEach(([symbol, values]) => {
            const series = ensureSeries(symbol);
            values.forEach(v => series.push(v));
        });
        lastList = persisted.list;
      previousLeaders = [...lastList].sort((a, b) => b.pChange - a.pChange).slice(0, 3).map(item => item.symbol);
        lastUpdatedAt = persisted.lastUpdatedAt;
        draw(lastList);
    }

    const taskId = `phase2-indices-${Math.random().toString(36).slice(2)}`;
    const unregister = chartRefreshScheduler.register(taskId, 'A', update, 1000);

    teardownMap.set(container, () => {
        unregister();
    });
}
